import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { UserIcon, BookIcon } from '../components/Icons';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

export const ActiveBetaReadersPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchApplications();
  }, []);

  const mapReadingSpeed = (speed: string) => {
    const speedMap: Record<string, string> = {
      SLOW: '6-8 weeks',
      MODERATE: '3-4 weeks',
      FAST: '2-3 weeks',
    };
    return speedMap[speed] || '4-6 weeks';
  };

  const mapExperienceLevel = (level: string) => {
    const levelMap: Record<string, string> = {
      BEGINNER: 'New to beta reading',
      INTERMEDIATE: 'Some beta reading experience',
      EXPERIENCED: 'Experienced beta reader',
    };
    return levelMap[level] || 'Not specified';
  };

  const fetchApplications = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.betaApplications.allApplications, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to fetch applications');
        setLoading(false);
        return;
      }

      console.log('[ActiveBetaReadersPage] Received data from API:', data);

      // Transform applications
      const transformedApplications = data.applications.map((app: any) => {
        console.log('[ActiveBetaReadersPage] Processing app:', {
          id: app.id,
          readingSpeed: app.readingSpeed,
          experienceLevel: app.experienceLevel,
          message: app.message
        });

        let frontendStatus = 'pending';
        if (app.status === 'PENDING') {
          frontendStatus = 'pending';
        } else if (app.status === 'ACCEPTED') {
          frontendStatus = 'accepted';
        } else if (app.status === 'REJECTED' || app.status === 'DECLINED') {
          frontendStatus = 'declined';
        }

        return {
          id: app.id,
          readerId: app.readerId,
          readerName: app.reader?.displayName || 'Unknown Reader',
          readerBio: app.reader?.bio || 'No bio provided',
          manuscriptId: app.manuscript?.id,
          manuscriptTitle: app.manuscript?.title || 'Unknown Manuscript',
          appliedDate: new Date(app.createdAt),
          status: frontendStatus,
          whyInterested: app.message || 'No message provided',
          estimatedTimeline: mapReadingSpeed(app.readingSpeed),
          previousExperience: mapExperienceLevel(app.experienceLevel),
        };
      });

      console.log('[ActiveBetaReadersPage] Transformed applications:', transformedApplications);

      setApplications(transformedApplications);
      setLoading(false);
    } catch (err) {
      console.error('Fetch applications error:', err);
      setError('Network error. Please check your connection and try again.');
      setLoading(false);
    }
  };

  // Format time since application
  const formatTimeSince = (date: Date) => {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 14) return '1 week ago';
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  // Filter applications by status and search query
  const filteredApplications = applications.filter(app => {
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    const matchesSearch =
      app.readerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.manuscriptTitle.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const handleAccept = async (applicationId: string, readerName: string) => {
    if (window.confirm(`Accept ${readerName} as a beta reader?`)) {
      try {
        const response = await fetch(API_ENDPOINTS.betaApplications.updateStatus(applicationId), {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify({ status: 'ACCEPTED' }),
        });

        const data = await response.json();

        if (!response.ok) {
          window.alert(data.error || 'Failed to accept application');
          return;
        }

        setApplications(applications.map(app =>
          app.id === applicationId ? { ...app, status: 'accepted' } : app
        ));
        window.alert(`${readerName} has been accepted! They will be notified via email.`);
      } catch (err) {
        console.error('Accept application error:', err);
        window.alert('Network error. Please try again.');
      }
    }
  };

  const handleDecline = async (applicationId: string, readerName: string) => {
    if (window.confirm(`Decline ${readerName}'s application?`)) {
      try {
        const response = await fetch(API_ENDPOINTS.betaApplications.updateStatus(applicationId), {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify({ status: 'REJECTED' }),
        });

        const data = await response.json();

        if (!response.ok) {
          window.alert(data.error || 'Failed to decline application');
          return;
        }

        setApplications(applications.map(app =>
          app.id === applicationId ? { ...app, status: 'declined' } : app
        ));
        window.alert(`${readerName}'s application has been declined.`);
      } catch (err) {
        console.error('Decline application error:', err);
        window.alert('Network error. Please try again.');
      }
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: any; label: string }> = {
      pending: { variant: 'warning' as const, label: 'Pending' },
      accepted: { variant: 'success' as const, label: 'Accepted' },
      declined: { variant: 'default' as const, label: 'Declined' },
    };
    const config = statusMap[status] || statusMap.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const pendingCount = applications.filter(app => app.status === 'pending').length;
  const acceptedCount = applications.filter(app => app.status === 'accepted').length;
  const declinedCount = applications.filter(app => app.status === 'declined').length;

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Page Header */}
        <div style={{
          marginBottom: '32px',
        }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Beta Reader Applications
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
            marginBottom: '12px',
          }}>
            Manage applications for all your manuscripts
          </p>
          <div style={{
            fontSize: '15px',
            color: theme.colors.textTertiary,
            fontFamily: theme.fonts.monospace,
          }}>
            {pendingCount} pending • {acceptedCount} accepted • {declinedCount} declined • {applications.length} total
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                Loading applications...
              </p>
            </div>
          </Card>
        )}

        {/* Error State */}
        {!loading && error && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.error,
                marginBottom: '20px',
              }}>
                {error}
              </p>
              <Button onClick={fetchApplications}>
                Try Again
              </Button>
            </div>
          </Card>
        )}

        {/* Status Filter and Search */}
        {!loading && !error && (
        <>
          <div style={{
            display: 'flex',
            gap: '16px',
            marginBottom: '24px',
            flexWrap: 'wrap',
          }}>
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '14px 56px 14px 16px',
                fontSize: '16px',
                border: `2px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.large,
                fontFamily: theme.fonts.primary,
                color: theme.colors.textPrimary,
                backgroundColor: theme.colors.white,
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              <option value="all">All Applications ({applications.length})</option>
              <option value="pending">Pending ({pendingCount})</option>
              <option value="accepted">Accepted ({acceptedCount})</option>
              <option value="declined">Declined ({declinedCount})</option>
            </select>

            {/* Search */}
            <input
              type="text"
              placeholder="Search by reader or manuscript..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                maxWidth: '400px',
                padding: '14px 16px',
                fontSize: '16px',
                border: `2px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.large,
                fontFamily: theme.fonts.primary,
                color: theme.colors.textPrimary,
                backgroundColor: theme.colors.white,
                outline: 'none',
              }}
            />
          </div>
        </>
        )}

        {/* Applications List - Empty State */}
        {!loading && !error && filteredApplications.length === 0 && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
                marginBottom: '20px',
              }}>
                {searchQuery || statusFilter !== 'all'
                  ? 'No applications match your filters'
                  : 'No applications yet'}
              </p>
              {!searchQuery && statusFilter === 'all' && (
                <Button onClick={() => window.location.href = '/writer/manuscripts'}>
                  View Your Manuscripts
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Applications List - Main Content */}
        {!loading && !error && filteredApplications.length > 0 && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}>
            {filteredApplications.map((application) => (
              <Card key={application.id}>
                <div style={{
                  display: 'flex',
                  gap: '24px',
                }}>
                  {/* Profile Photo */}
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: theme.colors.primaryLight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: theme.colors.primary,
                  }}>
                    <UserIcon width={40} height={40} />
                  </div>

                  {/* Application Details */}
                  <div style={{ flex: 1 }}>
                    {/* Header */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '12px',
                      gap: '16px',
                    }}>
                      <div>
                        <h3 style={{
                          fontSize: '22px',
                          fontWeight: '500',
                          marginBottom: '4px',
                          color: theme.colors.textPrimary,
                          cursor: 'pointer',
                        }}
                        onClick={() => window.location.href = `/reader/${application.readerId}`}
                        >
                          {application.readerName}
                        </h3>
                        <div style={{
                          fontSize: '14px',
                          color: theme.colors.textTertiary,
                          fontFamily: theme.fonts.monospace,
                        }}>
                          Applied {formatTimeSince(application.appliedDate)} • {application.manuscriptTitle}
                        </div>
                      </div>
                      {getStatusBadge(application.status)}
                    </div>

                    {/* Bio */}
                    <p style={{
                      fontSize: '15px',
                      lineHeight: '1.6',
                      color: theme.colors.textPrimary,
                      marginBottom: '16px',
                    }}>
                      {application.readerBio}
                    </p>

                    {/* Application Details */}
                    <div style={{
                      backgroundColor: theme.colors.backgroundAlt,
                      padding: '16px',
                      borderRadius: theme.borderRadius.medium,
                      marginBottom: '16px',
                    }}>
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: '500',
                          color: theme.colors.textSecondary,
                          marginBottom: '6px',
                        }}>
                          Why interested:
                        </div>
                        <p style={{
                          fontSize: '15px',
                          lineHeight: '1.6',
                          color: theme.colors.textPrimary,
                        }}>
                          {application.whyInterested}
                        </p>
                      </div>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '16px',
                        fontSize: '14px',
                      }}>
                        <div>
                          <span style={{ color: theme.colors.textSecondary }}>Timeline: </span>
                          <span style={{ color: theme.colors.textPrimary }}>{application.estimatedTimeline}</span>
                        </div>
                        <div>
                          <span style={{ color: theme.colors.textSecondary }}>Experience: </span>
                          <span style={{ color: theme.colors.textPrimary }}>{application.previousExperience}</span>
                        </div>
                      </div>

                      <div style={{
                        fontSize: '14px',
                        marginTop: '12px',
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}>
                          <BookIcon width={16} height={16} />
                          <span
                            style={{
                              color: theme.colors.primary,
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            }}
                            onClick={() => window.location.href = `/writer/manuscripts/${application.manuscriptId}`}
                          >
                            {application.manuscriptTitle}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    {application.status === 'pending' && (
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                      }}>
                        <Button
                          onClick={() => handleAccept(application.id, application.readerName)}
                        >
                          Accept
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleDecline(application.id, application.readerName)}
                        >
                          Decline
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => window.location.href = `/reader/${application.readerId}`}
                        >
                          View Full Profile
                        </Button>
                      </div>
                    )}

                    {application.status === 'accepted' && (
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                      }}>
                        <Button
                          variant="outline"
                          onClick={() => window.location.href = `/messages/new?to=${application.readerId}`}
                        >
                          Send Message
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => window.location.href = `/reader/${application.readerId}`}
                        >
                          View Profile
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};
